import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { ApiError } from '@mezha/api'
import { http } from './client'

/** Serializable error kept in the cache. */
export type ApiFailure = { status: number; code: ApiError['code']; message: string; details: Record<string, unknown> }

export type Req =
  | { get: string }
  | { command: string; body?: Record<string, unknown> }
  | { post: string; body?: unknown; auth?: boolean }

export const toFailure = (e: unknown): ApiFailure =>
  e instanceof ApiError
    ? { status: e.status, code: e.code, message: e.message, details: e.details }
    : { status: 0, code: 'unknown', message: e instanceof Error ? e.message : String(e), details: {} }

/** Rebuilds an ApiError from a cached failure (for texts and details). */
export const fromFailure = (f: ApiFailure): ApiError => new ApiError(f.status, f.code, f.message, f.details)

const baseQuery: BaseQueryFn<Req, unknown, ApiFailure> = async (req, api) => {
  try {
    if ('get' in req) return { data: await http.get(req.get, { signal: api.signal }) }
    if ('command' in req) return { data: await http.command(req.command, req.body ?? {}) }
    return { data: await http.post(req.post, req.body, { auth: req.auth }) }
  } catch (e) {
    return { error: toFailure(e) }
  }
}

export const TAGS = ['Me', 'Hero', 'Inventory', 'Loadout', 'World', 'Story', 'Board', 'Evidence', 'Battle', 'ActiveBattle', 'Arena', 'Rating',
  'Chat', 'Clans', 'Clan', 'Faith', 'Saga', 'Shop', 'Skills', 'Professions'] as const
export type Tag = (typeof TAGS)[number]

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: TAGS,
  refetchOnReconnect: true,
  endpoints: () => ({}),
})
